import { hasValue } from '@repo/utils';
import type { ECharts } from 'echarts';
import { action, computed, makeObservable, observable } from 'mobx';

import type { TrajectoryService } from '../../service/trajectory-service';
import type { TGraphTrajectoryView, TTrajectoryExcludeReason } from '../../types/graph-trajectory';
import type { TrajectoryVerticalDatum } from '../../types/state';
import { sortTrajectoryPointsByMd } from '../../utils/sort-trajectory-points-by-md';
import { MAIN_TRAJECTORY_LINE_WIDTH, REFERENCE_TRAJECTORY_LINE_WIDTH } from '../../utils/trajectory-line-colors';

import { buildGraphTrajectoryViews, formatGraphTrajectoryLegendLabel } from './build-graph-trajectory-views';
import {
  DEFAULT_VISIBLE_2D_CHART_IDS,
  getTrajectory2dChartConfig,
  TRAJECTORY_2D_CHART_IDS,
  type Trajectory2dChartConfig,
  type Trajectory2dChartTypeId,
} from './trajectory-2d-charts';
import { getReferenceExcludeReasonForChart } from './utils/chart-exclude-reason';
import { downloadChartScreenshot } from './utils/download-chart-screenshot';
import { readFiniteNumber, shiftPointForOverlay } from './utils/trajectory-offset-deltas';

export type Trajectory3dChartPoint = [we: number, ns: number, tvd: number];

export type Trajectory3dLine = {
  label: string;
  color: string;
  lineWidth: number;
  data: Trajectory3dChartPoint[];
};

export type Trajectory3dViewControlState = {
  alpha?: number;
  beta?: number;
  distance?: number;
  center?: number[];
};

export type TrajectoryChart2dSettings = {
  order: Trajectory2dChartTypeId[];
  visibility: Partial<Record<Trajectory2dChartTypeId, boolean>>;
};

export const MIN_VISIBLE_2D_CHARTS = 1;

function mapTrajectoryPoints3D(
  trajectory: TGraphTrajectoryView,
  verticalDatum: TrajectoryVerticalDatum,
): Trajectory3dChartPoint[] {
  const useTvdss = verticalDatum === 'tvdss';

  if (trajectory.role === 'reference') {
    const overlayReason = getReferenceExcludeReasonForChart(trajectory, {
      needsHorizontal: true,
      needsVertical: !useTvdss,
    });

    if (overlayReason) {
      return [];
    }
  }

  const points: Trajectory3dChartPoint[] = [];

  for (const point of sortTrajectoryPointsByMd(trajectory.entity.points)) {
    const shifted = shiftPointForOverlay(point.data, trajectory.deltas, {
      applyHorizontal: trajectory.role === 'reference',
      applyVertical: !useTvdss && trajectory.role === 'reference',
    });
    const vertical = useTvdss ? readFiniteNumber(point.data.tvdss) : shifted.tvd;

    if (shifted.we === null || shifted.ns === null || vertical === null) {
      continue;
    }

    points.push([shifted.we, shifted.ns, -vertical]);
  }

  return points;
}

function mergeChart2dOrder(savedOrder: Trajectory2dChartTypeId[] | undefined): Trajectory2dChartTypeId[] {
  const knownIds = new Set(TRAJECTORY_2D_CHART_IDS);
  const result: Trajectory2dChartTypeId[] = [];

  for (const id of savedOrder ?? []) {
    if (knownIds.has(id) && !result.includes(id)) {
      result.push(id);
    }
  }

  for (const id of TRAJECTORY_2D_CHART_IDS) {
    if (!result.includes(id)) {
      result.push(id);
    }
  }

  return result;
}

function buildDefaultVisibility(): Partial<Record<Trajectory2dChartTypeId, boolean>> {
  const visibility: Partial<Record<Trajectory2dChartTypeId, boolean>> = {};

  for (const id of TRAJECTORY_2D_CHART_IDS) {
    visibility[id] = DEFAULT_VISIBLE_2D_CHART_IDS.includes(id);
  }

  return visibility;
}

export class TrajectoryGraphStore {
  private readonly trajectoryService: TrajectoryService;

  @observable chartDimension: '2d' | '3d' = '2d';

  @observable verticalDatum: TrajectoryVerticalDatum = 'tvd';

  @observable.ref trajectory3dViewControl: Trajectory3dViewControlState | null = null;

  @observable.shallow chart2dOrder: Trajectory2dChartTypeId[] = [...TRAJECTORY_2D_CHART_IDS];

  @observable chart2dVisibility: Partial<Record<Trajectory2dChartTypeId, boolean>> = buildDefaultVisibility();

  @observable.ref private chartInstances: ECharts[] = [];

  /** Well names keyed by wellbore id (active + references), set by host. */
  @observable.ref wellNamesByWellboreId: ReadonlyMap<number, string> = new Map();

  /** Localized "active" marker for legend labels (set from React i18n). */
  @observable activeLegendLabel = 'active';

  constructor(trajectoryService: TrajectoryService) {
    this.trajectoryService = trajectoryService;

    makeObservable(this);
  }

  @computed
  get canDownloadScreenshot(): boolean {
    return this.chartInstances.length > 0;
  }

  @computed
  private get graphViewsBuild() {
    if (this.trajectoryService.isWellboreTrajectoriesLoading) {
      return { main: null, references: [] as TGraphTrajectoryView[], activeAziVertSectDeg: null };
    }

    return buildGraphTrajectoryViews({
      planOrFact: this.trajectoryService.planOrFact,
      selectedTrajectoryId: this.trajectoryService.selectedTrajectoryId,
      selectedWellboreId: this.trajectoryService.selectedWellboreId,
      wellboreTrajectories: this.trajectoryService.wellboreTrajectories,
      referenceEntitiesByWellbore: this.trajectoryService.referenceEntitiesByWellbore,
      referenceTrajectoryRequests: this.trajectoryService.referenceTrajectoryRequests,
      wellNamesByWellboreId: this.wellNamesByWellboreId,
    });
  }

  @computed
  get graphTrajectories(): TGraphTrajectoryView[] {
    const { main, references } = this.graphViewsBuild;

    if (!main) {
      return references;
    }

    return [main, ...references];
  }

  @computed
  get activeAziVertSectDeg(): number | null {
    return this.graphViewsBuild.activeAziVertSectDeg;
  }

  @computed
  get excluded3dWarnings(): Array<{ title: string; reason: TTrajectoryExcludeReason }> {
    return this.graphTrajectories
      .map((trajectory) => {
        const overlayReason = getReferenceExcludeReasonForChart(trajectory, {
          needsHorizontal: true,
          needsVertical: this.verticalDatum === 'tvd',
        });

        if (overlayReason) {
          return {
            title: formatGraphTrajectoryLegendLabel(trajectory, this.activeLegendLabel),
            reason: overlayReason,
          };
        }

        if (trajectory.entity.points.length > 0 && mapTrajectoryPoints3D(trajectory, this.verticalDatum).length === 0) {
          return {
            title: formatGraphTrajectoryLegendLabel(trajectory, this.activeLegendLabel),
            reason: 'missingPlotCoords' as const,
          };
        }

        return null;
      })
      .filter(hasValue);
  }

  /**
   * Deep plot dependency: station edits and aziRef changes invalidate 2D mapping
   * even when trajectory view identities stay the same.
   */
  @computed
  get graphPlotRevision() {
    return this.graphTrajectories.map((trajectory) => ({
      id: trajectory.id,
      title: trajectory.title,
      wellName: trajectory.wellName,
      color: trajectory.color,
      role: trajectory.role,
      deltas: trajectory.deltas,
      canPlotSpatial: trajectory.canPlotSpatial,
      canPlotVertical: trajectory.canPlotVertical,
      spatialExcludeReason: trajectory.spatialExcludeReason,
      verticalExcludeReason: trajectory.verticalExcludeReason,
      aziRef: trajectory.entity.data.aziRef,
      aziVertSect: trajectory.entity.data.aziVertSect,
      verticalDatum: this.verticalDatum,
      points: trajectory.entity.points.map((point) => point.data),
    }));
  }

  @computed
  get mainTrajectory3dLines(): Trajectory3dLine[] {
    return this.graphTrajectories
      .map((trajectory) => {
        const data = mapTrajectoryPoints3D(trajectory, this.verticalDatum);

        if (!data.length) {
          return null;
        }

        return {
          label: formatGraphTrajectoryLegendLabel(trajectory, this.activeLegendLabel),
          color: trajectory.color,
          lineWidth: trajectory.role === 'main' ? MAIN_TRAJECTORY_LINE_WIDTH : REFERENCE_TRAJECTORY_LINE_WIDTH,
          data,
        } satisfies Trajectory3dLine;
      })
      .filter(hasValue);
  }

  @computed
  get chart2dSettings(): TrajectoryChart2dSettings {
    return {
      order: [...this.chart2dOrder],
      visibility: { ...this.chart2dVisibility },
    };
  }

  @computed
  get enabledChart2dConfigs(): Trajectory2dChartConfig[] {
    return this.chart2dOrder
      .filter((id) => this.chart2dVisibility[id] !== false)
      .map((id) => getTrajectory2dChartConfig(id))
      .filter(hasValue);
  }

  @computed
  get visible2dChartsCount(): number {
    return this.enabledChart2dConfigs.length;
  }

  @action.bound
  setWellNamesByWellboreId(names: ReadonlyMap<number, string>) {
    this.wellNamesByWellboreId = names;
  }

  @action.bound
  setActiveLegendLabel(label: string) {
    this.activeLegendLabel = label;
  }

  @action.bound
  setChartDimension(dimension: '2d' | '3d') {
    this.chartDimension = dimension;
  }

  @action.bound
  setVerticalDatum(verticalDatum: TrajectoryVerticalDatum) {
    this.verticalDatum = verticalDatum;
  }

  @action.bound
  setTrajectory3dViewControl(viewControl: Trajectory3dViewControlState) {
    this.trajectory3dViewControl = viewControl;
  }

  @action.bound
  setChart2dSettings(settings: TrajectoryChart2dSettings | undefined) {
    this.chart2dOrder = mergeChart2dOrder(settings?.order);

    if (settings?.visibility) {
      const visibility = buildDefaultVisibility();

      for (const id of TRAJECTORY_2D_CHART_IDS) {
        if (settings.visibility[id] !== undefined) {
          visibility[id] = settings.visibility[id];
        }
      }

      // Ensure at least one chart stays visible.
      if (!TRAJECTORY_2D_CHART_IDS.some((id) => visibility[id] !== false)) {
        visibility[DEFAULT_VISIBLE_2D_CHART_IDS[0]] = true;
      }

      this.chart2dVisibility = visibility;
    } else {
      this.chart2dVisibility = buildDefaultVisibility();
    }
  }

  @action.bound
  setChart2dOrder(order: Trajectory2dChartTypeId[]) {
    this.chart2dOrder = mergeChart2dOrder(order);
  }

  @action.bound
  setChart2dVisibility(id: Trajectory2dChartTypeId, visible: boolean) {
    if (!visible && this.visible2dChartsCount <= MIN_VISIBLE_2D_CHARTS && this.chart2dVisibility[id] !== false) {
      return;
    }

    this.chart2dVisibility = {
      ...this.chart2dVisibility,
      [id]: visible,
    };
  }

  isChart2dVisibilityDisabled(id: Trajectory2dChartTypeId): boolean {
    const isVisible = this.chart2dVisibility[id] !== false;

    return isVisible && this.visible2dChartsCount <= MIN_VISIBLE_2D_CHARTS;
  }

  @action.bound
  registerChartInstance(chart: ECharts) {
    if (!this.chartInstances.includes(chart)) {
      this.chartInstances = [...this.chartInstances, chart];
    }
  }

  @action.bound
  unregisterChartInstance(chart: ECharts) {
    this.chartInstances = this.chartInstances.filter((instance) => instance !== chart);
  }

  @action.bound
  downloadScreenshot() {
    if (!this.chartInstances.length) {
      return;
    }

    const chartsInVisualOrder = [...this.chartInstances].sort((left, right) => {
      const leftRect = left.getDom().getBoundingClientRect();
      const rightRect = right.getDom().getBoundingClientRect();

      return leftRect.top - rightRect.top || leftRect.left - rightRect.left;
    });

    downloadChartScreenshot(chartsInVisualOrder, `trajectory-${this.chartDimension}`);
  }
}
